import Styles from './ProductGridCard.module.scss';
import React, { useContext, useEffect, useState} from 'react';
import FeatherIcon from 'feather-icons-react';
import { Popover } from '@mui/material';

// Components import.
import { ViewContext } from 'components/lib';
import { Button } from '@mui/material';
import { MilitaryTech } from '@mui/icons-material';
import Badge, { BADGE_TYPES } from 'components/global/CardBadge/CardBadge';

/**
 * 
 * @param {boolean} minimal if true then shows a shrunk version of the card
 * @returns 
 */

const ProductGridCard = ({ tooltipMessage, data, minimal = false, isInternship = false, ...props }) => {
    const viewContext = useContext(ViewContext)

    const [anchorEl, setAnchorEl] = useState(null);

    const isPopoverOpen = Boolean(anchorEl);

    const chipColorMap = {
        "Completed": Styles.completedChip,
        "Continue Course": Styles.continueCourseChip,
        "Trial Ended": Styles.trialEndedChip,
        "Start Course": Styles.startCourse,
        "In Certifications": Styles.inCertification,
        "License Expired": Styles.trialEndedChip,
        "Continue Certificate": Styles.continueCourseChip,
        "In Internships": Styles.inInternships
    };

    // Close popover on scroll
    useEffect(() => {
        if (!isPopoverOpen) return;

        const handleScroll = () => {
            setAnchorEl(null);
        };

        // capture = true → catches scroll from any container
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isPopoverOpen]);

    const resolveBadgeConfig = (data) => {
        if (!data) return null;

        if (data.is_new) {
            return {
                type: BADGE_TYPES.INFO,
                label: 'New',
            };
        }

        switch (data.subscriptionStatus) {
            case 'trial':
                return {
                    type: BADGE_TYPES.SUCCESS,
                    label: 'On Trial',
                };

            case 'trial_expired':
                return {
                    type: BADGE_TYPES.ERROR,
                    label: 'Purchase Required',
                };

            case 'disabled':
                return {
                    type: BADGE_TYPES.WARN,
                    label: 'Pending Renewal',
                };

            default:
                return null;
        }
    }

    const badgeConfig = resolveBadgeConfig(data);

    const buttonChipClass =
        data.isFailed
            ? Styles.failedChip
            : chipColorMap[data.buttonText] || Styles.startCourse;

    //converting the due date into dd-mm-yyy format
    const dueDate = data?.due_date && new Date(data?.due_date);
    const parsedDueDate = data?.due_date && dueDate.toLocaleDateString(
        'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }
    );

    const purchaseDate = data?.purchase_date && new Date(data?.purchase_date);
    const parsedPurchaseDate = data?.purchase_date && purchaseDate.toLocaleDateString(
        'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }
    );

    const tooltipText =
        isInternship
            ? viewContext.t("Components.ProductGridCard.internshipPrompt")
            : viewContext.t("Components.ProductGridCard.certPrompt");
    return (
        <>
            <div
                id="gridAncestor"
                data-testid="ProductGridCard-gridAncestor"
                className={Styles.container}
                onClick={(e) => {
                    e.preventDefault();
                    // If card should show popover
                    if (isInternship || data?.hasOwnProperty("cert_id")) {
                        setAnchorEl(e.currentTarget);
                    }
                    // Otherwise normal navigation
                    props.onClick?.(data, e);
                }}

            >
                <div data-testid="ProductGridCard-card" className={`${Styles.card} ${data.subscriptionStatus === 'disabled' ? Styles.cardDisabled : ''}`}>
                    {/* TOP BADGE */}
                    {props?.showBadge && badgeConfig && (
                        <Badge type={badgeConfig?.type}>
                            {badgeConfig?.label}
                        </Badge>
                    )}

                    {/* TOP ROW: Badge left, Date right */}
                    <div data-testid="ProductGridCard-card-top-row" className={Styles.topRow}>
                        {/* {data.badgeText && <div className={Styles.badge}>{data.badgeText}</div>} */}
                        {data?.due_date ? (
                            <span
                                title={viewContext.t('Components.ProductGridCard.dueDate')}
                                className={Styles.dueDate}
                            >
                                <FeatherIcon
                                    icon="clock"
                                    size="12px"
                                    style={{ color: 'grey', marginRight: '4px' }}
                                />
                                {parsedPurchaseDate && parsedDueDate
                                    ? `${parsedPurchaseDate} - ${parsedDueDate}`
                                    : parsedDueDate}
                            </span>
                        ) : (
                            <div className={Styles.dueDate} />
                        )}
                    </div>


                    {/* IMAGE BLOCK */}
                    <div data-testid="ProductGridCard-image-wrapper" className={`${Styles.imageWrapper} ${data.subscriptionStatus === 'disabled' ? Styles.imageWrapperDisabled : ''}`}>
                        <div data-testid="ProductGridCard-image-box" className={`${Styles.imageBox} ${data.subscriptionStatus === 'disabled' ? Styles.imageBoxDisabled : ''}`}>
                            <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${data.icon || data.image}`} alt={data.name} />
                        </div>
                    </div>

                    {/* TEXT BLOCK */}
                    <div data-testid="ProductGridCard-card-content" className={`${Styles.cardContent} ${data.subscriptionStatus === 'disabled' ? Styles.cardContentDisabled : ''}`}>
                        <h3 className={Styles.title}>{data.name}</h3>

                        {!minimal && <p className={Styles.description} data-testid="ProductCard-description">{data.descr || data.description}</p>}

                        {/* METRICS */}
                        <div data-testid="ProductGridCard-metrics" className={Styles.metrics}>
                            {/* SCORE */}
                            { data.score != null && (<div data-testid="ProductGridCard-scoreRow" className={Styles.scoreRow} style={{ display: props.showInfoChips ? "" : "none" }}>
                                <div data-testid="ProductGridCard-score-text" className={Styles.scoreLeft}>
                                    <span className={Styles.scoreLabel}>{viewContext.t('Components.ProductGridCard.score')}</span>
                                    <span className={Styles.scoreValue}>
                                        {data.score}/{data.totalScore ?? 10}
                                    </span>
                                </div>
                            </div>)}

                            {/* PROGRESS BAR */}
                            {props.showProgressBar && (
                                <div data-testid="ProductGridCard-progress-box" className={Styles.progressBlock}>
                                    <div data-testid="ProductGridCard-progress-header" className={Styles.progressHeader}>
                                        <span>{viewContext.t('Components.ProductGridCard.progress')}</span>
                                        <span className={Styles.progressValue}>{data.completion_percentage}%</span>
                                    </div>
                                    <div data-testid="ProductGridCard-progress-header" className={Styles.progressBar}>
                                        <div data-testid="ProductGridCard-progress-fill"
                                            className={Styles.progressFill}
                                            style={{ width: `${data.completion_percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            {/* STATUS + BADGES */}
                            <div data-testid="ProductGridCard-status-row" className={Styles.statusRow}>
                                <div data-testid="ProductGridCard-status-button" className={Styles.buttonContainer}>
                                    <span
                                        data-testid="ProductGridCard-status-chip"
                                        className={`${Styles.statusChip} ${buttonChipClass}`}
                                        style={{
                                            opacity: data.isDisabled ? 0.6 : 1,
                                            pointerEvents: data.isDisabled ? "none" : "auto",
                                            width: "auto",
                                            textAlign: "center"
                                        }}
                                    >
                                        {viewContext.t(`Components.ProductGridCard.button.${data.buttonText}`)}
                                    </span>
                                </div>
                                {!!props?.showMedalChips && (
                                    <div data-testid="ProductGridCard-badge-list" className={Styles.badgeList}>
                                        <MilitaryTech fontSize='large' sx={{ color: '#F7C706', opacity: data.badge === 3 ? 1 : 0.3 }} />
                                        <MilitaryTech fontSize='large' sx={{ color: '#8D9297', opacity: data.badge === 2 ? 1 : 0.3 }} />
                                        <MilitaryTech fontSize='large' sx={{ color: '#CE8946', opacity: data.badge === 1 ? 1 : 0.3 }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                disableRestoreFocus
                disableScrollLock
            >
                <div className={Styles.tooltipMain}>
                    <p className={Styles.frmCert}>
                        {tooltipText}
                    </p>

                    {props?.isPartOfOtherProducts?.map((item, index) => (
                        <div key={index} style={{ width: '100%' }}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    setAnchorEl(null);
                                    props?.onCertTooltipClick?.(
                                        item?.id,
                                        item?.isInternship
                                    );
                                }}
                                disabled={item?.isExpired}
                            >
                                {item?.name}
                            </Button>
                        </div>
                    ))}
                </div>
            </Popover>

        </>
    );
}

export default ProductGridCard;